// src/utils/exportRealEstateOwnersDetailed.js

import ExcelJS from "exceljs";
import {
  getDetailedRealEstateData,
  getDetailedDataStats
} from "@/components/OneVrAnalytics/AnalyticsDashboardStyles/AnalyticsDashboardChilds/detailedRealEstateData";

/**
 * Export Real Estate Owners Detailed 15-Minute Interval Data to Excel
 * Creates comprehensive tables with all KPI data at 15-minute intervals
 */
export async function exportDetailedRealEstateToExcel(filters = {}) {
  try {
    const stats = getDetailedDataStats();
    const currentDate = new Date().toLocaleDateString("en-GB");

    // Get filtered data
    let detailedData;
    if (filters.month) {
      detailedData = getDetailedRealEstateData(filters.month);
    } else {
      detailedData = getDetailedRealEstateData();
    }

    // Flatten to array if single month
    const monthsData = Array.isArray(detailedData)
      ? detailedData
      : [detailedData];

    // Create workbook
    const workbook = new ExcelJS.Workbook();
    workbook.creator = "OneVR Analytics - Detailed View";
    workbook.created = new Date();
    workbook.modified = new Date();

    // ==================== SUMMARY SHEET ====================
    const summarySheet = workbook.addWorksheet("Summary", {
      properties: { tabColor: { argb: "FF1E40AF" } }
    });

    createSummarySheet(summarySheet, stats, filters);

    // ==================== ENERGY CONSUMPTION DETAILS ====================
    const energySheet = workbook.addWorksheet("Energy Consumption Details", {
      properties: { tabColor: { argb: "FFF59E0B" } }
    });

    createEnergyDetailsTable(energySheet, monthsData);

    // ==================== CARBON EMISSIONS DETAILS ====================
    const carbonSheet = workbook.addWorksheet("Carbon Emissions Details", {
      properties: { tabColor: { argb: "FFEF4444" } }
    });

    createCarbonDetailsTable(carbonSheet, monthsData);

    // ==================== ESG SCORE DETAILS ====================
    const esgSheet = workbook.addWorksheet("ESG Score Details", {
      properties: { tabColor: { argb: "FF10B981" } }
    });

    createEsgDetailsTable(esgSheet, monthsData);

    // ==================== OCCUPANCY RATE DETAILS ====================
    const occupancySheet = workbook.addWorksheet("Occupancy Rate Details", {
      properties: { tabColor: { argb: "FF3B82F6" } }
    });

    createOccupancyDetailsTable(occupancySheet, monthsData);

    // ==================== OPERATIONAL COST DETAILS ====================
    const operationalSheet = workbook.addWorksheet("Operational Cost Details", {
      properties: { tabColor: { argb: "FF8B5CF6" } }
    });

    createOperationalDetailsTable(operationalSheet, monthsData);

    // Generate and download
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;

    const filterSuffix = filters.day
      ? `Day-${filters.day}`
      : filters.month
      ? `Month-${filters.month}`
      : `Year-${filters.year || "All"}`;
    link.download = `RealEstate-Detailed-15min-${filterSuffix}-${currentDate.replace(
      /\//g,
      "-"
    )}.xlsx`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    return { success: true, filename: link.download };
  } catch (error) {
    console.error("Error exporting detailed data to Excel:", error);
    return { success: false, error: error.message };
  }
}

// ==================== HELPER FUNCTIONS ====================

function createTableHeader(sheet, headers, color) {
  headers.forEach((header, index) => {
    const cell = sheet.getCell(1, index + 1);
    cell.value = header;
    cell.font = { bold: true, size: 11, color: { argb: "FFFFFFFF" } };
    cell.alignment = {
      vertical: "middle",
      horizontal: "center",
      wrapText: true
    };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: color } };
    cell.border = {
      top: { style: "medium", color: { argb: "FF000000" } },
      bottom: { style: "medium", color: { argb: "FF000000" } },
      left: { style: "thin", color: { argb: "FFFFFFFF" } },
      right: { style: "thin", color: { argb: "FFFFFFFF" } }
    };
  });
  sheet.getRow(1).height = 40;
}

function styleDataCell(cell, value, isEven, format = "text") {
  cell.value = value;
  cell.alignment = { vertical: "middle", horizontal: "center" };
  cell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: isEven ? "FFFFFFFF" : "FFF9FAFB" }
  };
  cell.border = {
    top: { style: "thin", color: { argb: "FFE5E7EB" } },
    bottom: { style: "thin", color: { argb: "FFE5E7EB" } },
    left: { style: "thin", color: { argb: "FFE5E7EB" } },
    right: { style: "thin", color: { argb: "FFE5E7EB" } }
  };

  if (format === "number") {
    cell.numFmt = "#,##0";
  } else if (format === "decimal") {
    cell.numFmt = "0.0";
  } else if (format === "currency") {
    cell.numFmt = "€#,##0.00";
  } else if (format === "percent") {
    cell.numFmt = '0.0"%"';
  } else if (format === "time") {
    cell.numFmt = "hh:mm";
  }
}

function createSummarySheet(sheet, stats, filters) {
  sheet.columns = [{ width: 30 }, { width: 50 }];

  // Title
  sheet.mergeCells("A1:B1");
  const titleCell = sheet.getCell("A1");
  titleCell.value = "DETAILED 15-MINUTE INTERVAL DATA - SUMMARY";
  titleCell.font = { size: 16, bold: true, color: { argb: "FFFFFFFF" } };
  titleCell.alignment = { vertical: "middle", horizontal: "center" };
  titleCell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FF1E40AF" }
  };
  sheet.getRow(1).height = 35;

  let row = 3;
  addDataRow(sheet, row++, "Report Type", "Detailed 15-Minute Intervals");
  addDataRow(sheet, row++, "Total Months", stats.totalMonths);
  addDataRow(sheet, row++, "Total Business Days", stats.totalBusinessDays);
  addDataRow(
    sheet,
    row++,
    "Total Data Points (15-min intervals)",
    stats.totalBookingIntervals?.toLocaleString()
  );
  addDataRow(sheet, row++, "Interval Duration", stats.intervalDuration);
  addDataRow(sheet, row++, "Operating Hours", stats.operatingHours);
  addDataRow(
    sheet,
    row++,
    "Date Range",
    `${stats.dateRange.start} to ${stats.dateRange.end}`
  );

  row++;
  addSectionHeader(sheet, row++, "Applied Filters");
  addDataRow(sheet, row++, "Year", filters.year || "All Available");
  addDataRow(sheet, row++, "Month", filters.month || "All Available");
  addDataRow(sheet, row++, "Day", filters.day || "All Available");

  row++;
  addDataRow(
    sheet,
    row++,
    "Report Generated",
    new Date().toLocaleDateString("en-GB")
  );
}

function addDataRow(sheet, rowNum, label, value) {
  sheet.getCell(`A${rowNum}`).value = label;
  sheet.getCell(`A${rowNum}`).font = {
    bold: true,
    size: 11,
    color: { argb: "FF374151" }
  };
  sheet.getCell(`A${rowNum}`).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFF3F4F6" }
  };

  sheet.getCell(`B${rowNum}`).value = value;
  sheet.getCell(`B${rowNum}`).font = { size: 11, color: { argb: "FF1F2937" } };

  sheet.getRow(rowNum).height = 20;
}

function addSectionHeader(sheet, rowNum, text) {
  sheet.mergeCells(`A${rowNum}:B${rowNum}`);
  const cell = sheet.getCell(`A${rowNum}`);
  cell.value = text;
  cell.font = { bold: true, size: 12, color: { argb: "FFFFFFFF" } };
  cell.alignment = { vertical: "middle", horizontal: "center" };
  cell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FF6B7280" }
  };
  sheet.getRow(rowNum).height = 25;
}

function createEnergyDetailsTable(sheet, monthsData) {
  sheet.columns = [
    { width: 12 }, // Date
    { width: 12 }, // Day
    { width: 8 }, // Time
    { width: 12 }, // Total kWh
    { width: 12 }, // Electricity kWh
    { width: 10 }, // Gas kWh
    { width: 10 }, // Cost €
    { width: 12 }, // Intensity
    { width: 12 }, // Peak kWh
    { width: 15 } // Top Appliances %
  ];

  const headers = [
    "Date",
    "Day of Week",
    "Time",
    "Total Energy\n(kWh)",
    "Electricity\n(kWh)",
    "Gas\n(kWh)",
    "Cost\n(€)",
    "Intensity\n(kWh/sqm)",
    "Peak\n(kWh)",
    "Top Appliances\nShare %"
  ];

  createTableHeader(sheet, headers, "FFF59E0B");

  let rowNum = 2;
  monthsData.forEach((month) => {
    month.dailyData.forEach((day) => {
      day.energyIntervals.forEach((interval, index) => {
        const isEven = rowNum % 2 === 0;

        styleDataCell(sheet.getCell(rowNum, 1), day.date, isEven);
        styleDataCell(sheet.getCell(rowNum, 2), day.dayOfWeek, isEven);
        styleDataCell(sheet.getCell(rowNum, 3), interval.time, isEven, "time");
        styleDataCell(
          sheet.getCell(rowNum, 4),
          interval.totalKwh,
          isEven,
          "decimal"
        );
        styleDataCell(
          sheet.getCell(rowNum, 5),
          interval.electricityKwh,
          isEven,
          "decimal"
        );
        styleDataCell(
          sheet.getCell(rowNum, 6),
          interval.gasKwh,
          isEven,
          "decimal"
        );
        styleDataCell(
          sheet.getCell(rowNum, 7),
          interval.cost,
          isEven,
          "currency"
        );
        styleDataCell(
          sheet.getCell(rowNum, 8),
          day.dailySummary.energy.intensity,
          isEven,
          "decimal"
        );
        styleDataCell(
          sheet.getCell(rowNum, 9),
          day.dailySummary.energy.peakKwh,
          isEven,
          "decimal"
        );
        styleDataCell(
          sheet.getCell(rowNum, 10),
          day.dailySummary.energy.topAppliancesShare,
          isEven,
          "decimal"
        );

        sheet.getRow(rowNum).height = 18;
        rowNum++;
      });
    });
  });
}

function createCarbonDetailsTable(sheet, monthsData) {
  sheet.columns = [
    { width: 12 }, // Date
    { width: 12 }, // Day
    { width: 8 }, // Time
    { width: 15 }, // Total Emissions
    { width: 12 }, // Gauge Value
    { width: 10 }, // Floor 1 %
    { width: 10 }, // Floor 2 %
    { width: 10 }, // Floor 3 %
    { width: 10 } // Floor 4 %
  ];

  const headers = [
    "Date",
    "Day of Week",
    "Time",
    "Total Emissions\n(kg CO₂)",
    "Gauge Value\n%",
    "Floor 1\n%",
    "Floor 2\n%",
    "Floor 3\n%",
    "Floor 4\n%"
  ];

  createTableHeader(sheet, headers, "FFEF4444");

  let rowNum = 2;
  monthsData.forEach((month) => {
    month.dailyData.forEach((day) => {
      day.bookings.forEach((booking, index) => {
        const isEven = rowNum % 2 === 0;

        styleDataCell(sheet.getCell(rowNum, 1), day.date, isEven);
        styleDataCell(sheet.getCell(rowNum, 2), day.dayOfWeek, isEven);
        styleDataCell(sheet.getCell(rowNum, 3), booking.time, isEven, "time");
        styleDataCell(
          sheet.getCell(rowNum, 4),
          day.dailySummary.carbon.totalEmissions,
          isEven,
          "number"
        );
        styleDataCell(
          sheet.getCell(rowNum, 5),
          day.dailySummary.carbon.gaugeValue,
          isEven,
          "decimal"
        );

        day.dailySummary.carbon.floorBreakdown.forEach((floor, floorIndex) => {
          styleDataCell(
            sheet.getCell(rowNum, 6 + floorIndex),
            floor.percentage,
            isEven,
            "decimal"
          );
        });

        sheet.getRow(rowNum).height = 18;
        rowNum++;
      });
    });
  });
}

function createEsgDetailsTable(sheet, monthsData) {
  sheet.columns = [
    { width: 12 }, // Date
    { width: 12 }, // Day
    { width: 8 }, // Time
    { width: 12 }, // Overall Score
    { width: 12 }, // Gauge Value
    { width: 10 }, // Lobby %
    { width: 12 }, // Conference %
    { width: 12 }, // Meeting %
    { width: 12 }, // Open Office %
    { width: 12 }, // CO2 Reporting
    { width: 12 }, // Energy Tracking
    { width: 14 }, // GRESB
    { width: 12 } // ISO14001
  ];

  const headers = [
    "Date",
    "Day of Week",
    "Time",
    "Overall Score\n%",
    "Gauge Value\n%",
    "Lobby\n%",
    "Conference\nRoom %",
    "Meeting\nRoom %",
    "Open\nOffice %",
    "CO2\nReporting",
    "Energy\nTracking",
    "GRESB\nIntegration",
    "ISO14001"
  ];

  createTableHeader(sheet, headers, "FF10B981");

  let rowNum = 2;
  monthsData.forEach((month) => {
    month.dailyData.forEach((day) => {
      day.bookings.forEach((booking, index) => {
        const isEven = rowNum % 2 === 0;

        styleDataCell(sheet.getCell(rowNum, 1), day.date, isEven);
        styleDataCell(sheet.getCell(rowNum, 2), day.dayOfWeek, isEven);
        styleDataCell(sheet.getCell(rowNum, 3), booking.time, isEven, "time");
        styleDataCell(
          sheet.getCell(rowNum, 4),
          day.dailySummary.esg.overallScore,
          isEven,
          "decimal"
        );
        styleDataCell(
          sheet.getCell(rowNum, 5),
          day.dailySummary.esg.gaugeValue,
          isEven,
          "decimal"
        );
        styleDataCell(
          sheet.getCell(rowNum, 6),
          day.dailySummary.esg.spaceAllocation.lobby,
          isEven,
          "number"
        );
        styleDataCell(
          sheet.getCell(rowNum, 7),
          day.dailySummary.esg.spaceAllocation.conferenceRoom,
          isEven,
          "number"
        );
        styleDataCell(
          sheet.getCell(rowNum, 8),
          day.dailySummary.esg.spaceAllocation.meetingRoom,
          isEven,
          "number"
        );
        styleDataCell(
          sheet.getCell(rowNum, 9),
          day.dailySummary.esg.spaceAllocation.openOffice,
          isEven,
          "number"
        );
        styleDataCell(
          sheet.getCell(rowNum, 10),
          day.dailySummary.esg.compliance.co2Reporting ? "YES" : "NO",
          isEven
        );
        styleDataCell(
          sheet.getCell(rowNum, 11),
          day.dailySummary.esg.compliance.energyTracking ? "YES" : "NO",
          isEven
        );
        styleDataCell(
          sheet.getCell(rowNum, 12),
          day.dailySummary.esg.compliance.gresbIntegration ? "YES" : "NO",
          isEven
        );
        styleDataCell(
          sheet.getCell(rowNum, 13),
          day.dailySummary.esg.compliance.iso14001 ? "YES" : "NO",
          isEven
        );

        sheet.getRow(rowNum).height = 18;
        rowNum++;
      });
    });
  });
}

function createOccupancyDetailsTable(sheet, monthsData) {
  sheet.columns = [
    { width: 12 }, // Date
    { width: 12 }, // Day
    { width: 8 }, // Time
    { width: 12 }, // Occupancy Rate
    { width: 12 }, // People Onsite
    { width: 12 }, // Peak Rate
    { width: 12 }, // Open Office %
    { width: 12 }, // Meeting Room %
    { width: 10 }, // Lobby %
    { width: 12 }, // Conference %
    { width: 15 } // Space Utilization
  ];

  const headers = [
    "Date",
    "Day of Week",
    "Time",
    "Occupancy\nRate %",
    "People\nOnsite",
    "Peak\nRate %",
    "Open\nOffice %",
    "Meeting\nRoom %",
    "Lobby\n%",
    "Conference\nRoom %",
    "Space\nUtilization %"
  ];

  createTableHeader(sheet, headers, "FF3B82F6");

  let rowNum = 2;
  monthsData.forEach((month) => {
    month.dailyData.forEach((day) => {
      day.bookings.forEach((booking, index) => {
        const isEven = rowNum % 2 === 0;

        styleDataCell(sheet.getCell(rowNum, 1), day.date, isEven);
        styleDataCell(sheet.getCell(rowNum, 2), day.dayOfWeek, isEven);
        styleDataCell(sheet.getCell(rowNum, 3), booking.time, isEven, "time");
        styleDataCell(
          sheet.getCell(rowNum, 4),
          booking.occupancyRate,
          isEven,
          "decimal"
        );
        styleDataCell(
          sheet.getCell(rowNum, 5),
          booking.peopleOnsite,
          isEven,
          "number"
        );
        styleDataCell(
          sheet.getCell(rowNum, 6),
          day.dailySummary.occupancy.peak,
          isEven,
          "decimal"
        );
        styleDataCell(
          sheet.getCell(rowNum, 7),
          booking.spaceUtilization.openOffice,
          isEven,
          "decimal"
        );
        styleDataCell(
          sheet.getCell(rowNum, 8),
          booking.spaceUtilization.meetingRoom,
          isEven,
          "decimal"
        );
        styleDataCell(
          sheet.getCell(rowNum, 9),
          booking.spaceUtilization.lobby,
          isEven,
          "decimal"
        );
        styleDataCell(
          sheet.getCell(rowNum, 10),
          booking.spaceUtilization.conferenceRoom,
          isEven,
          "decimal"
        );
        styleDataCell(
          sheet.getCell(rowNum, 11),
          day.dailySummary.occupancy.spaceUtilization,
          isEven,
          "decimal"
        );

        sheet.getRow(rowNum).height = 18;
        rowNum++;
      });
    });
  });
}

function createOperationalDetailsTable(sheet, monthsData) {
  sheet.columns = [
    { width: 12 }, // Date
    { width: 12 }, // Day
    { width: 8 }, // Time
    { width: 15 }, // Energy Analytics
    { width: 15 }, // Dynamic Occupancy
    { width: 18 }, // Utilization Analytics
    { width: 12 }, // Open Office %
    { width: 12 }, // Meeting Room %
    { width: 10 }, // Lobby %
    { width: 12 } // Conference %
  ];

  const headers = [
    "Date",
    "Day of Week",
    "Time",
    "Energy\nAnalytics %",
    "Dynamic\nOccupancy %",
    "Utilization\nAnalytics %",
    "Open\nOffice %",
    "Meeting\nRoom %",
    "Lobby\n%",
    "Conference\nRoom %"
  ];

  createTableHeader(sheet, headers, "FF8B5CF6");

  let rowNum = 2;
  monthsData.forEach((month) => {
    month.dailyData.forEach((day) => {
      day.bookings.forEach((booking, index) => {
        const isEven = rowNum % 2 === 0;

        styleDataCell(sheet.getCell(rowNum, 1), day.date, isEven);
        styleDataCell(sheet.getCell(rowNum, 2), day.dayOfWeek, isEven);
        styleDataCell(sheet.getCell(rowNum, 3), booking.time, isEven, "time");
        styleDataCell(
          sheet.getCell(rowNum, 4),
          day.dailySummary.operational.energyAnalytics,
          isEven,
          "decimal"
        );
        styleDataCell(
          sheet.getCell(rowNum, 5),
          day.dailySummary.operational.dynamicOccupancy,
          isEven,
          "decimal"
        );
        styleDataCell(
          sheet.getCell(rowNum, 6),
          day.dailySummary.operational.utilizationAnalytics,
          isEven,
          "decimal"
        );
        styleDataCell(
          sheet.getCell(rowNum, 7),
          day.dailySummary.operational.spaceEfficiency.openOffice,
          isEven,
          "number"
        );
        styleDataCell(
          sheet.getCell(rowNum, 8),
          day.dailySummary.operational.spaceEfficiency.meetingRoom,
          isEven,
          "number"
        );
        styleDataCell(
          sheet.getCell(rowNum, 9),
          day.dailySummary.operational.spaceEfficiency.lobby,
          isEven,
          "number"
        );
        styleDataCell(
          sheet.getCell(rowNum, 10),
          day.dailySummary.operational.spaceEfficiency.conferenceRoom,
          isEven,
          "number"
        );

        sheet.getRow(rowNum).height = 18;
        rowNum++;
      });
    });
  });
}
